import type { AdminCapability, OrganizationSummary, Permission, RestrictedOrganization } from "../api/contract";
import { prefs } from "../stores/prefs.svelte";
import type { session as SessionStore } from "../stores/session.svelte";
import { can, hasFeature } from "./permissions";
import type { RouteMatch, RouteName } from "../../router.svelte";

export type Resolution =
  | { kind: "loading" }
  | { kind: "redirect"; to: string; replace: boolean }
  | { kind: "public"; name: RouteName }
  | { kind: "admin"; name: RouteName }
  | { kind: "admin-denied"; name: RouteName; capability: AdminCapability }
  | { kind: "app"; name: RouteName; orgId: string }
  | { kind: "denied"; name: RouteName; orgId: string; permission: Permission }
  /** Membership exists but the current network is blocked by the org's IP policy. */
  | { kind: "restricted"; organization: RestrictedOrganization }
  | { kind: "notfound" };

/**
 * Where a given organization's portal actually starts. An unseated ordinary member has
 * no `portal.overview` at all, so sending them to `/overview` would bounce forever;
 * their landing page is the unlicensed notice instead.
 */
export function landingPath(org: OrganizationSummary | undefined | null, orgId: string): string {
  if (org && !hasFeature(org, "portal.overview") && hasFeature(org, "portal.unlicensed")) {
    return `/orgs/${orgId}/unlicensed`;
  }
  if (org && !hasFeature(org, "portal.overview") && hasFeature(org, "portal.people")) {
    return `/orgs/${orgId}/users`;
  }
  return `/orgs/${orgId}/overview`;
}

/** Pick the org to land on: last used if still valid, else the first org. */
function defaultOrgId(session: typeof SessionStore): string | null {
  if (prefs.lastOrgId && session.orgById(prefs.lastOrgId)) return prefs.lastOrgId;
  return session.organizations[0]?.id ?? null;
}

function defaultDestination(session: typeof SessionStore): string | null {
  const orgId = defaultOrgId(session);
  if (orgId) return landingPath(session.orgById(orgId), orgId);
  // No reachable org, but the account does belong to one the network blocks: land on
  // it so the reason is explained instead of showing the "no purchases yet" screen.
  const restricted = session.restrictedOrganizations[0];
  return restricted ? `/orgs/${restricted.id}/overview` : null;
}

/** Substitute `:param` placeholders in a superseded route's redirect target. */
function fillPattern(pattern: string, params: Record<string, string>): string {
  return pattern.replace(/:([A-Za-z0-9_]+)/g, (match, key: string) =>
    params[key] === undefined ? match : encodeURIComponent(params[key]!)
  );
}

export function resolveNavigation(match: RouteMatch, session: typeof SessionStore): Resolution {
  const { def, name, params } = match;

  if (name === "notfound") return { kind: "notfound" };

  // Public routes with no session requirement render immediately.
  if (def.scope === "public" && !def.guard.session) {
    return { kind: "public", name };
  }

  // Superseded paths redirect before any session work so a deep link keeps its target
  // through the login round trip (the new path is what ends up in `returnTo`).
  if (def.guard.redirectTo) {
    const search = match.query.toString();
    return {
      kind: "redirect",
      to: fillPattern(def.guard.redirectTo, params) + (search ? `?${search}` : ""),
      replace: true
    };
  }

  // Session-guarded (root + all app routes) must wait for the bootstrap probe.
  if (!session.ready) return { kind: "loading" };
  if (!session.authenticated) {
    if (name === "root") return { kind: "redirect", to: "/login", replace: true };
    const search = match.query.toString();
    const returnTo = encodeURIComponent(match.path + (search ? `?${search}` : ""));
    return { kind: "redirect", to: `/login?returnTo=${returnTo}`, replace: true };
  }

  if (name === "root") {
    if (session.canManageEntitlements) {
      return { kind: "redirect", to: "/admin/entitlements", replace: true };
    }
    const destination = defaultDestination(session);
    // A verified account with no matching entitlement is a first-class state, not an
    // error: land on the welcome/empty-account screen instead of bouncing out.
    if (!destination) return { kind: "redirect", to: "/welcome", replace: true };
    return { kind: "redirect", to: destination, replace: true };
  }

  if (name === "welcome") {
    if (session.canManageEntitlements) {
      return { kind: "redirect", to: "/admin/entitlements", replace: true };
    }
    // Once an org exists there's nothing to welcome into — go to the portal.
    const destination = defaultDestination(session);
    if (destination) return { kind: "redirect", to: destination, replace: true };
    return { kind: "public", name };
  }

  // Authenticated entry flows (for example extension-directed sign-in) use the
  // public shell but intentionally require a settled portal session.
  if (def.scope === "public") return { kind: "public", name };

  if (def.scope === "admin") {
    if (!session.canManageEntitlements) return { kind: "notfound" };
    // Keep the denial inside the admin shell so the operator retains navigation and
    // account controls. The rendered state stays generic and does not confirm that
    // any inaccessible resource exists.
    if (def.guard.capability && !session.hasCapability(def.guard.capability)) {
      return { kind: "admin-denied", name, capability: def.guard.capability };
    }
    return { kind: "admin", name };
  }

  // App routes: resolve org, feature, then capability.
  const orgId = params.orgId ?? "";
  const org = session.orgById(orgId);
  if (!org) {
    // Network-restricted organizations are never navigable: the session deliberately
    // omits them, so there is no data to render and nothing to retry in-place.
    const restricted = session.restrictedOrgById(orgId);
    if (restricted) return { kind: "restricted", organization: restricted };
    const destination = defaultDestination(session);
    return { kind: "redirect", to: destination ?? "/welcome", replace: true };
  }

  if (def.guard.feature && !hasFeature(org, def.guard.feature)) {
    // Feature not enabled for this org/role — the nav item is hidden; bounce to the
    // org's real landing page (which is NOT always the overview).
    const home = landingPath(org, orgId);
    // Guard against a self-redirect if the landing page's own feature is missing.
    if (home === match.path) return { kind: "notfound" };
    return { kind: "redirect", to: home, replace: true };
  }

  if (def.guard.permission && !can(org, def.guard.permission)) {
    // Capability denial renders in-place within the shell (never a redirect).
    return { kind: "denied", name, orgId, permission: def.guard.permission };
  }

  return { kind: "app", name, orgId };
}
